<script setup lang="ts">
    import { computed } from 'vue';
    import { floatsApproxEqual } from '@/domain/geometry/approxEquals';

    interface Band {
        min: number;
        max: number;
    }

    const props = withDefaults(
        defineProps<{
            modelValue: number;
            min: number;
            max: number;
            step?: number;
            disabled?: boolean;
            label: string;
            safeBand: Band | null;
            surgeonBounds: Band | null;
            combinedVersionMark: number | null;
            isOutOfSafeZone: boolean;
        }>(),
        {
            step: 1,
            disabled: false,
        },
    );

    const emit = defineEmits<{
        'update:modelValue': [number];
    }>();

    function clampToRange(value: number): number {
        return Math.min(props.max, Math.max(props.min, value));
    }

    function snapToStep(value: number): number {
        return Math.round(value / props.step) * props.step;
    }

    function increment() {
        const next = clampToRange(snapToStep(props.modelValue) + props.step);
        if (!floatsApproxEqual(next, props.modelValue, 0.01)) {
            emit('update:modelValue', next);
        }
    }

    function decrement() {
        const next = clampToRange(snapToStep(props.modelValue) - props.step);
        if (!floatsApproxEqual(next, props.modelValue, 0.01)) {
            emit('update:modelValue', next);
        }
    }

    const displayValue = computed(() => snapToStep(props.modelValue));

    const minDisabled = computed(() => floatsApproxEqual(props.modelValue, props.min, 0.01));
    const maxDisabled = computed(() => floatsApproxEqual(props.modelValue, props.max, 0.01));

    function pct(value: number): number {
        return ((value - props.min) / (props.max - props.min)) * 100;
    }

    const currentPct = computed(() => pct(clampToRange(props.modelValue)));

    function pctClamped(value: number): number {
        return Math.min(100, Math.max(0, pct(value)));
    }

    const safeBandStyle = computed(() => {
        if (!props.safeBand) return null;
        const left = pctClamped(props.safeBand.min);
        const right = pctClamped(props.safeBand.max);
        return { left: `${left}%`, width: `${right - left}%` };
    });

    const surgeonBoundsStyle = computed(() => {
        if (!props.surgeonBounds) return null;
        const left = pctClamped(props.surgeonBounds.min);
        const right = pctClamped(props.surgeonBounds.max);
        return { left: `${left}%`, width: `${right - left}%` };
    });

    const combinedVersionStyle = computed(() => {
        if (props.combinedVersionMark == null) return null;
        if (props.combinedVersionMark < props.min || props.combinedVersionMark > props.max) return null;
        return { left: `${pct(props.combinedVersionMark)}%` };
    });

    const markerAtCombinedVersion = computed(() => {
        if (props.combinedVersionMark == null) return false;
        const threshold = props.step > 0 ? props.step / 2 : 0.5;
        return Math.abs(props.modelValue - props.combinedVersionMark) < threshold;
    });
</script>

<template>
    <div class="safe-zone-slider" :class="{ 'out-of-zone': isOutOfSafeZone }">
        <div class="label-row">
            <span class="label">{{ label }}</span>
        </div>
        <div class="slider-row pt-3">
            <div class="pill-track" data-testid="safe-zone-slider-track">
                <div
                    class="value-label"
                    :class="{ 'at-combined-version': markerAtCombinedVersion }"
                    :style="{ left: currentPct + '%' }"
                >
                    {{ displayValue }}
                </div>
                <div class="bands-clip">
                    <div
                        v-if="surgeonBoundsStyle"
                        class="surgeon-band"
                        data-testid="safe-zone-slider-surgeon-band"
                        :style="surgeonBoundsStyle"
                    />
                    <div
                        v-if="safeBandStyle"
                        class="safe-band"
                        data-testid="safe-zone-slider-safe-band"
                        :style="safeBandStyle"
                    />
                    <div
                        v-if="combinedVersionStyle"
                        class="combined-version-tick"
                        data-testid="safe-zone-slider-combined-version"
                        :style="combinedVersionStyle"
                    />
                    <div
                        class="current-marker"
                        :class="{ 'at-combined-version': markerAtCombinedVersion }"
                        data-testid="safe-zone-slider-current-marker"
                        :style="{ left: currentPct + '%' }"
                    />
                </div>
            </div>
            <div class="button-group">
                <v-btn
                    size="48"
                    class="plus-minus-button minus"
                    elevation="1"
                    :disabled="disabled || minDisabled"
                    data-testid="safe-zone-slider-minus"
                    @click="decrement"
                >
                    <v-icon size="24" icon="mdi-minus" color="primary" />
                </v-btn>
                <v-btn
                    size="48"
                    class="plus-minus-button plus"
                    elevation="1.5"
                    :disabled="disabled || maxDisabled"
                    data-testid="safe-zone-slider-plus"
                    @click="increment"
                >
                    <v-icon size="24" icon="mdi-plus" color="primary" />
                </v-btn>
            </div>
            <div class="range-labels">
                <span class="min-label">{{ min }}</span>
                <span class="max-label">{{ max }}</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .safe-zone-slider {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 100%;
    }

    .label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .label {
        color: rgb(var(--v-theme-secondary-text));
        font-size: 16px;
    }

    .slider-row {
        display: grid;
        grid-template-columns: 1fr auto;
        column-gap: 16px;
        row-gap: 4px;
        align-items: center;
    }

    .pill-track {
        position: relative;
        grid-column: 1;
        grid-row: 1;
        height: 35px;
        border-radius: 24px;
        border: 1px solid rgb(var(--v-theme-outline));
        background: rgb(var(--v-theme-surface));
        overflow: visible;
    }

    .bands-clip {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        overflow: hidden;
    }

    .current-marker {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        transform: translateX(-50%);
        background: rgb(var(--v-theme-secondary-text));
    }

    .value-label {
        position: absolute;
        top: -20px;
        transform: translateX(-50%);
        font-weight: 700;
        font-size: 13px;
        color: rgb(var(--v-theme-secondary-text));
    }

    .button-group {
        grid-column: 2;
        grid-row: 1;
        display: flex;
        gap: 12px;
    }

    .plus-minus-button {
        border-radius: 16px;
    }

    .range-labels {
        grid-column: 1;
        grid-row: 2;
        display: flex;
        justify-content: space-between;
        font-size: 13px;
        color: rgb(var(--v-theme-secondary));
    }

    .safe-zone-slider.out-of-zone .pill-track {
        border-color: rgb(var(--v-theme-red-hover));
        background: rgba(var(--v-theme-red), 0.84);
    }

    .surgeon-band {
        position: absolute;
        top: 0;
        bottom: 0;
        background: rgb(var(--v-theme-outline));
    }

    .safe-band {
        position: absolute;
        top: 0;
        bottom: 0;
        background: rgb(var(--v-theme-green));
    }

    .combined-version-tick {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        transform: translateX(-50%);
        background: rgb(var(--v-theme-primary));
    }

    .current-marker.at-combined-version {
        background: rgb(var(--v-theme-primary));
    }

    .value-label.at-combined-version {
        color: rgb(var(--v-theme-primary));
    }
</style>
